import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { farmProduct } from "../../assets/farmProduct";

const EMAILJS_SERVICE = "YOUR_SERVICE_ID";
const EMAILJS_TEMPLATE = "YOUR_TEMPLATE_ID";
const EMAILJS_PUBLIC = "YOUR_PUBLIC_KEY";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to   { opacity: 1; transform: scale(1); }
`;

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "specs", label: "Specifications" },
  { key: "features", label: "Key Features" },
  { key: "shipping", label: "Shipping" },
];

const EquipmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const formRef = useRef();

  const [equipment, setEquipment] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);

  /* ── FIX: separate found/not-found states so we never
     navigate away before the lookup has actually run ── */
  useEffect(() => {
    const found = farmProduct.find((item) => String(item.id) === String(id));
    if (found) {
      setEquipment(found);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [id]);

  /* Only redirect once we know it's genuinely missing */
  useEffect(() => {
    if (notFound) {
      navigate("/equipments", { replace: true });
    }
  }, [notFound, navigate]);

  /* ── IMAGE HELPERS ── */
  const images = equipment?.images || [];

  const openLightbox = (idx) => {
    setLightboxIdx(idx);
    setLightbox(true);
  };
  const closeLightbox = () => setLightbox(false);
  const lbNext = () => setLightboxIdx((p) => (p + 1) % images.length);
  const lbPrev = () =>
    setLightboxIdx((p) => (p - 1 + images.length) % images.length);

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") lbNext();
      if (e.key === "ArrowLeft") lbPrev();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  /* ── FORM SUBMIT ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE,
        EMAILJS_TEMPLATE,
        formRef.current,
        EMAILJS_PUBLIC,
      );
      setSubmitStatus("success");
      formRef.current.reset();
      setTimeout(() => {
        setSubmitStatus(null);
        setQuoteOpen(false);
      }, 3000);
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── VISIBLE TABS ── */
  const visibleTabs = TABS.filter(({ key }) => {
    if (!equipment) return false;
    if (key === "specs" && !equipment.specifications?.length) return false;
    if (key === "features" && !equipment.keyFeatures?.length) return false;
    if (key === "shipping" && !equipment.shipping) return false;
    return true;
  });

  /* ── LOADING / NOT FOUND ── */
  if (!equipment) {
    return (
      <TheEquipmentDetails>
        <div className="loading">
          <span>Loading</span>
        </div>
      </TheEquipmentDetails>
    );
  }

  const shippingEntries = equipment.shipping
    ? Object.values(equipment.shipping)
    : [];

  return (
    <>
      <Helmet>
        <title>AgroHarvest | {equipment.name}</title>
        <meta name="description" content={equipment.description} />
      </Helmet>

      <TheEquipmentDetails>
        {/* ══ HERO ══ */}
        <div className="detail-hero">
          <div className="hero-image">
            <img
              src={images[0]}
              alt={equipment.name}
              onLoad={() => setHeroLoaded(true)}
            />
          </div>
          <div className="hero-overlay" />
          <div className={`hero-content ${heroLoaded ? "visible" : ""}`}>
            <button className="back-btn" onClick={() => navigate(-1)}>
              &#8592; Back to Equipment
            </button>
            <div className="hero-meta">
              <span className="hero-label">
                AgroHarvest Machineries — Equipment Detail
              </span>
              <h1>{equipment.name}</h1>
              <div className="hero-tags">
                {equipment.stockNumber && (
                  <span className="hero-tag">
                    Stock #{equipment.stockNumber}
                  </span>
                )}
                {equipment.category && (
                  <span className="hero-tag">{equipment.category}</span>
                )}
              </div>
            </div>
          </div>
          <div className="hero-rule" />
        </div>

        {/* ══ MAIN BODY ══ */}
        <div className="body-grid">
          {/* ── LEFT: Gallery + Tabs ── */}
          <div className="left-col">
            {/* Gallery */}
            <div className="gallery">
              <div
                className="gallery-main"
                onClick={() => openLightbox(selectedImg)}
                title="Click to enlarge"
              >
                <img src={images[selectedImg]} alt={equipment.name} />
                <span className="zoom-hint">&#8599; Enlarge</span>
              </div>

              {images.length > 1 && (
                <div className="gallery-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      className={`thumb ${selectedImg === i ? "active" : ""}`}
                      onClick={() => setSelectedImg(i)}
                    >
                      <img src={img} alt={`View ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Tab bar */}
            <div className="tab-bar">
              {visibleTabs.map(({ key, label }) => (
                <button
                  key={key}
                  className={`tab-btn ${activeTab === key ? "active" : ""}`}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Tab panels */}
            <div className="tab-panel" key={activeTab}>
              {activeTab === "overview" && (
                <div className="panel-overview">
                  <span className="section-tag">Description</span>
                  <p>{equipment.description}</p>
                </div>
              )}

              {activeTab === "specs" &&
                equipment.specifications?.length > 0 && (
                  <div className="panel-specs">
                    <span className="section-tag">
                      Technical Specifications
                    </span>
                    <ul className="spec-list">
                      {equipment.specifications.map((spec, i) => (
                        <li key={i}>
                          <span className="spec-num">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span>{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {activeTab === "features" &&
                equipment.keyFeatures?.length > 0 && (
                  <div className="panel-features">
                    <span className="section-tag">Key Features</span>
                    <ul className="feature-list">
                      {equipment.keyFeatures.map((f, i) => (
                        <li key={i}>
                          <span className="feat-check">&#10003;</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {activeTab === "shipping" && shippingEntries.length > 0 && (
                <div className="panel-shipping">
                  <span className="section-tag">Shipping Policy</span>
                  <ul className="shipping-list">
                    {shippingEntries.map((val, i) => (
                      <li key={i}>
                        <span className="shipping-dot" />
                        <p>{val}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Info panel ── */}
          <div className="right-col">
            <div className="info-card">
              <div className="info-name-block">
                <span className="section-tag">Equipment</span>
                <h2>{equipment.name}</h2>
              </div>

              <div className="info-rows">
                {equipment.stockNumber && (
                  <div className="info-row">
                    <span className="info-label">Stock #</span>
                    <span className="info-value mono">
                      {equipment.stockNumber}
                    </span>
                  </div>
                )}
                {equipment.category && (
                  <div className="info-row">
                    <span className="info-label">Condition</span>
                    <span className="info-value">{equipment.category}</span>
                  </div>
                )}
                {equipment.price && (
                  <div className="info-row">
                    <span className="info-label">MOQ</span>
                    <span className="info-value price-val">
                      {/* ${Number(equipment.price).toLocaleString()} */} 3 Units
                    </span>
                  </div>
                )}
              </div>

              <button className="quote-btn" onClick={() => setQuoteOpen(true)}>
                Request a Quote
              </button>

              <p className="info-note">
                Every machine is reconditioned to AgroHarvest standards. Contact
                us for bulk pricing and availability.
              </p>
            </div>
          </div>
        </div>

        {/* ══ LIGHTBOX ══ */}
        {lightbox && (
          <LightboxOverlay onClick={closeLightbox}>
            <div className="lb-inner" onClick={(e) => e.stopPropagation()}>
              <button className="lb-close" onClick={closeLightbox}>
                &#10005;
              </button>
              <button className="lb-nav lb-prev" onClick={lbPrev}>
                &#8592;
              </button>
              <div className="lb-image">
                <img
                  src={images[lightboxIdx]}
                  alt={`View ${lightboxIdx + 1}`}
                />
              </div>
              <button className="lb-nav lb-next" onClick={lbNext}>
                &#8594;
              </button>
              <div className="lb-counter">
                {lightboxIdx + 1} / {images.length}
              </div>
            </div>
          </LightboxOverlay>
        )}

        {/* ══ QUOTE MODAL ══ */}
        {quoteOpen && (
          <ModalOverlay onClick={() => setQuoteOpen(false)}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <span className="section-tag">Price Enquiry</span>
                  <h3>{equipment.name}</h3>
                </div>
                <button
                  className="modal-close"
                  onClick={() => setQuoteOpen(false)}
                >
                  &#10005;
                </button>
              </div>

              {submitStatus === "success" ? (
                <div className="modal-success">
                  <span className="success-mark">&#10003;</span>
                  <h4>Enquiry Sent</h4>
                  <p>Our team will reach out within 24 hours.</p>
                </div>
              ) : (
                <form
                  ref={formRef}
                  className="modal-form"
                  onSubmit={handleSubmit}
                >
                  <input
                    type="hidden"
                    name="equipment_name"
                    value={equipment.name}
                  />
                  <input
                    type="hidden"
                    name="stock_number"
                    value={equipment.stockNumber || ""}
                  />

                  <div className="mf-group">
                    <label>
                      Full Name <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="from_name"
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="mf-group">
                    <label>
                      Email Address <span>*</span>
                    </label>
                    <input
                      type="email"
                      name="reply_to"
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="mf-group">
                    <label>
                      Phone Number <span>*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div className="mf-group">
                    <label>Message</label>
                    <textarea
                      name="message"
                      rows="3"
                      placeholder="Additional details or questions..."
                    />
                  </div>

                  {submitStatus === "error" && (
                    <p className="form-error">
                      Something went wrong. Please try again.
                    </p>
                  )}

                  <button
                    type="submit"
                    className="modal-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Submit Enquiry"}
                  </button>
                </form>
              )}
            </div>
          </ModalOverlay>
        )}
      </TheEquipmentDetails>
    </>
  );
};

/* ══════════════════════════════════════════
   LIGHTBOX
══════════════════════════════════════════ */
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;

  .lb-inner {
    position: relative;
    width: 90vw;
    max-width: 1100px;
    height: 85vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lb-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${scaleIn} 0.25s ease;
    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  .lb-close {
    position: absolute;
    top: -2.5rem;
    right: 0;
    background: none;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
    width: 36px;
    height: 36px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: background 0.2s;
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }

  .lb-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    width: 44px;
    height: 44px;
    cursor: pointer;
    font-size: 1rem;
    transition: background 0.2s;
    z-index: 10;
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
    &.lb-prev {
      left: -3.5rem;
    }
    &.lb-next {
      right: -3.5rem;
    }
  }

  .lb-counter {
    position: absolute;
    bottom: -2.2rem;
    left: 50%;
    transform: translateX(-50%);
    font-family: "Courier New", monospace;
    font-size: 0.68rem;
    letter-spacing: 0.14em;
    color: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 600px) {
    .lb-nav {
      &.lb-prev {
        left: 0.5rem;
      }
      &.lb-next {
        right: 0.5rem;
      }
    }
  }
`;

/* ══════════════════════════════════════════
   MODAL
══════════════════════════════════════════ */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.2s ease;

  .modal-box {
    background: #f5f2ed;
    border: 1px solid #d5d0c8;
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    overflow-y: auto;
    animation: ${scaleIn} 0.25s ease;
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1.8rem 2rem 1.2rem;
    border-bottom: 1px solid #d5d0c8;

    h3 {
      font-size: 1rem;
      font-weight: 400;
      color: #1c1c1c;
      margin: 0;
      line-height: 1.4;
      max-width: 330px;
      font-family: "Georgia", serif;
    }

    .modal-close {
      background: none;
      border: none;
      font-size: 0.9rem;
      color: #7a7265;
      cursor: pointer;
      padding: 0;
      margin-left: 1rem;
      flex-shrink: 0;
      transition: color 0.2s;
      &:hover {
        color: #1c1c1c;
      }
    }
  }

  .modal-form {
    padding: 1.8rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 1.1rem;

    .mf-group {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;

      label {
        font-family: "Courier New", monospace;
        font-size: 0.65rem;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #7a7265;
        span {
          color: #999;
        }
      }

      input,
      textarea {
        padding: 0.72rem 0.9rem;
        border: 1px solid #d5d0c8;
        background: #fff;
        font-size: 0.9rem;
        font-family: "Georgia", serif;
        color: #1c1c1c;
        transition: border-color 0.2s;
        border-radius: 0;
        &::placeholder {
          color: #b0a898;
        }
        &:focus {
          outline: none;
          border-color: #1c1c1c;
        }
      }

      textarea {
        resize: vertical;
      }
    }

    .form-error {
      font-family: "Courier New", monospace;
      font-size: 0.7rem;
      color: #b94040;
      margin: 0;
    }

    .modal-submit {
      background: #1c1c1c;
      color: #f5f2ed;
      border: none;
      padding: 0.9rem;
      font-family: "Courier New", monospace;
      font-size: 0.72rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      cursor: pointer;
      transition: background 0.2s;
      &:hover:not(:disabled) {
        background: #333;
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .modal-success {
    padding: 3rem 2rem;
    text-align: center;
    font-family: "Georgia", serif;

    .success-mark {
      display: block;
      font-size: 1.6rem;
      color: #1c1c1c;
      margin-bottom: 1rem;
    }

    h4 {
      font-size: 1.1rem;
      font-weight: 400;
      color: #1c1c1c;
      margin: 0 0 0.5rem;
    }

    p {
      font-size: 0.88rem;
      color: #7a7265;
      font-style: italic;
      margin: 0;
    }
  }

  @media (max-width: 480px) {
    .modal-header,
    .modal-form {
      padding: 1.2rem;
    }
  }
`;

/* ══════════════════════════════════════════
   MAIN STYLED COMPONENT
══════════════════════════════════════════ */
const TheEquipmentDetails = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f2ed;
  font-family: "Georgia", "Times New Roman", serif;
  color: #1c1c1c;

  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    span {
      font-family: "Courier New", monospace;
      font-size: 0.72rem;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #7a7265;
      animation: ${fadeIn} 1s ease infinite alternate;
    }
  }

  .detail-hero {
    position: relative;
    width: 100%;
    height: 62vh;
    overflow: hidden;

    .hero-image {
      position: absolute;
      inset: 0;
      z-index: 1;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 8s ease;
      }
    }

    &:hover .hero-image img {
      transform: scale(1.04);
    }

    .hero-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(
        105deg,
        rgba(0, 0, 0, 0.85) 0%,
        rgba(0, 0, 0, 0.5) 55%,
        rgba(0, 0, 0, 0.2) 100%
      );
      z-index: 2;
    }

    .hero-content {
      position: absolute;
      inset: 0;
      z-index: 3;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 2rem 6rem 3.5rem;
      opacity: 0;
      transform: translateY(10px);
      transition:
        opacity 0.7s ease,
        transform 0.7s ease;

      &.visible {
        opacity: 1;
        transform: translateY(0);
      }

      .back-btn {
        display: inline-block;
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.35);
        color: rgba(255, 255, 255, 0.75);
        padding: 0.45rem 1rem;
        font-family: "Courier New", monospace;
        font-size: 0.68rem;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        cursor: pointer;
        transition: all 0.2s;
        align-self: flex-start;
        &:hover {
          background: rgba(255, 255, 255, 0.12);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.6);
        }
      }

      .hero-meta {
        .hero-label {
          display: block;
          font-family: "Courier New", monospace;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.45);
          margin-bottom: 1rem;
        }

        h1 {
          font-size: clamp(1.8rem, 4vw, 3.8rem);
          font-weight: 400;
          color: #f5f2ed;
          line-height: 1.1;
          letter-spacing: -0.02em;
          margin: 0 0 1.2rem;
          max-width: 700px;
        }

        .hero-tags {
          display: flex;
          gap: 0.6rem;
          flex-wrap: wrap;

          .hero-tag {
            font-family: "Courier New", monospace;
            font-size: 0.65rem;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: rgba(255, 255, 255, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.25);
            padding: 0.25rem 0.7rem;
          }
        }
      }
    }

    .hero-rule {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
      z-index: 3;
    }
  }

  .body-grid {
    max-width: 1200px;
    margin: 0 auto;
    padding: 4rem 4rem 6rem;
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 4rem;
    align-items: start;
  }

  .section-tag {
    display: block;
    font-family: "Courier New", monospace;
    font-size: 0.68rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #7a7265;
    margin-bottom: 1rem;
  }

  .gallery {
    margin-bottom: 2.5rem;

    .gallery-main {
      position: relative;
      width: 100%;
      aspect-ratio: 4 / 3;
      background: #e8e4dc;
      overflow: hidden;
      cursor: zoom-in;
      border: 1px solid #d5d0c8;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.5s ease;
      }

      .zoom-hint {
        position: absolute;
        bottom: 0.8rem;
        right: 0.9rem;
        font-family: "Courier New", monospace;
        font-size: 0.62rem;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
        background: rgba(0, 0, 0, 0.4);
        padding: 0.25rem 0.6rem;
        opacity: 0;
        transition: opacity 0.2s;
      }

      &:hover {
        img {
          transform: scale(1.03);
        }
        .zoom-hint {
          opacity: 1;
        }
      }
    }

    .gallery-thumbs {
      display: flex;
      gap: 0.5rem;
      margin-top: 0.6rem;
      flex-wrap: wrap;

      .thumb {
        width: 72px;
        height: 54px;
        border: 1px solid #d5d0c8;
        background: #e8e4dc;
        padding: 0;
        cursor: pointer;
        overflow: hidden;
        transition: border-color 0.2s;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }
        &:hover img {
          transform: scale(1.08);
        }
        &.active,
        &:hover {
          border-color: var(--primary-color);
        }
      }
    }
  }

  .tab-bar {
    display: flex;
    border-top: 1px solid #d5d0c8;
    border-bottom: 1px solid #d5d0c8;
    margin-bottom: 2rem;

    .tab-btn {
      flex: 1;
      padding: 0.85rem 0.5rem;
      background: transparent;
      border: none;
      border-right: 1px solid #d5d0c8;
      font-family: "Courier New", monospace;
      font-size: 0.65rem;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #7a7265;
      cursor: pointer;
      transition: all 0.2s;
      &:last-child {
        border-right: none;
      }
      &:hover {
        background: #ede8e0;
        color: #1c1c1c;
      }
      &.active {
        background: #1c1c1c;
        color: #f5f2ed;
      }
    }
  }

  .tab-panel {
    animation: ${fadeUp} 0.3s ease;
    min-height: 180px;
    p {
      font-size: 0.97rem;
      line-height: 1.85;
      color: #3d3d3d;
      margin: 0;
    }
  }

  .spec-list {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      display: grid;
      grid-template-columns: 30px 1fr;
      gap: 1rem;
      padding: 0.85rem 0;
      border-bottom: 1px solid #ede8e0;
      font-size: 0.93rem;
      color: #3d3d3d;
      line-height: 1.5;
      &:first-child {
        padding-top: 0;
      }
      &:last-child {
        border-bottom: none;
      }
      .spec-num {
        font-family: "Courier New", monospace;
        font-size: 0.62rem;
        letter-spacing: 0.1em;
        color: #b0a898;
        padding-top: 0.12rem;
      }
    }
  }

  .feature-list {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      display: grid;
      grid-template-columns: 22px 1fr;
      gap: 0.8rem;
      padding: 0.75rem 0;
      border-bottom: 1px solid #ede8e0;
      font-size: 0.93rem;
      color: #3d3d3d;
      &:first-child {
        padding-top: 0;
      }
      &:last-child {
        border-bottom: none;
      }
      .feat-check {
        font-family: "Courier New", monospace;
        font-size: 0.75rem;
        color: var(--primary-color);
        padding-top: 0.1rem;
      }
    }
  }

  .shipping-list {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      display: grid;
      grid-template-columns: 14px 1fr;
      gap: 0.9rem;
      padding: 0.8rem 0;
      border-bottom: 1px solid #ede8e0;
      align-items: baseline;
      &:first-child {
        padding-top: 0;
      }
      &:last-child {
        border-bottom: none;
      }
      .shipping-dot {
        width: 4px;
        height: 4px;
        background: #b0a898;
        border-radius: 50%;
        margin-top: 0.55rem;
        flex-shrink: 0;
      }
      p {
        font-size: 0.9rem;
        line-height: 1.72;
        color: #3d3d3d;
        margin: 0;
      }
    }
  }

  .info-card {
    border: 1px solid #d5d0c8;
    background: #fff;
    padding: 2rem;
    position: sticky;
    top: 2rem;
    animation: ${fadeUp} 0.5s ease 0.2s both;

    .info-name-block {
      margin-bottom: 1.5rem;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid #d5d0c8;
      h2 {
        font-size: 1.15rem;
        font-weight: 400;
        color: #1c1c1c;
        margin: 0;
        line-height: 1.35;
      }
    }

    .info-rows {
      display: flex;
      flex-direction: column;
      margin-bottom: 2rem;

      .info-row {
        display: grid;
        grid-template-columns: 90px 1fr;
        gap: 0.5rem;
        padding: 0.8rem 0;
        border-bottom: 1px solid #ede8e0;
        align-items: center;
        &:last-child {
          border-bottom: none;
        }

        .info-label {
          font-family: "Courier New", monospace;
          font-size: 0.65rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #7a7265;
        }
        .info-value {
          font-size: 0.93rem;
          color: #1c1c1c;
          &.mono {
            font-family: "Courier New", monospace;
            font-size: 0.78rem;
            letter-spacing: 0.05em;
          }
          &.price-val {
            font-size: 1.25rem;
            font-family: "Courier New", monospace;
            color: var(--primary-color);
            letter-spacing: -0.01em;
          }
        }
      }
    }

    .quote-btn {
      width: 100%;
      background: var(--primary-color);
      color: #f5f2ed;
      border: none;
      padding: 1rem;
      font-family: "Courier New", monospace;
      font-size: 0.75rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      cursor: pointer;
      transition: opacity 0.2s;
      margin-bottom: 1.2rem;
      &:hover {
        opacity: 0.85;
      }
    }

    .info-note {
      font-size: 0.8rem;
      color: #7a7265;
      font-style: italic;
      line-height: 1.6;
      margin: 0;
      border-left: 2px solid #d5d0c8;
      padding-left: 0.9rem;
    }
  }

  @media screen and (max-width: 1100px) {
    .body-grid {
      padding: 3.5rem 3rem 5rem;
      gap: 3rem;
      grid-template-columns: 1fr 300px;
    }
  }

  @media screen and (max-width: 900px) {
    .detail-hero {
      height: 52vh;
      .hero-content {
        padding: 1.8rem 2.5rem 3rem;
      }
    }
    .body-grid {
      grid-template-columns: 1fr;
      padding: 3rem 2rem 4rem;
      gap: 0;
    }
    .right-col {
      margin-top: 2.5rem;
      padding-top: 2.5rem;
      border-top: 1px solid #d5d0c8;
    }
    .info-card {
      position: static;
    }
  }

  @media screen and (max-width: 600px) {
    .detail-hero {
      height: 48vh;
      .hero-content {
        padding: 1.5rem 1.8rem 2.5rem;
        h1 {
          font-size: 1.7rem;
        }
      }
    }
    .body-grid {
      padding: 2.5rem 1.5rem 3rem;
    }
    .tab-bar .tab-btn {
      font-size: 0.58rem;
      padding: 0.75rem 0.3rem;
      letter-spacing: 0.08em;
    }
  }

  @media screen and (max-width: 420px) {
    .detail-hero {
      height: 44vh;
      .hero-content {
        padding: 1.2rem 1.4rem 2rem;
        h1 {
          font-size: 1.4rem;
        }
      }
    }
    .body-grid {
      padding: 2rem 1.2rem 2.5rem;
    }
    .info-card {
      padding: 1.5rem;
    }
  }

  @media screen and (max-width: 350px) {
    .detail-hero {
      height: 40vh;
      .hero-content h1 {
        font-size: 1.2rem;
      }
    }
  }
`;

export default EquipmentDetails;
